import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import {
  buildMenuQrCopy,
  IMenuQrCopy,
  resolveMenuQrCopyLocale,
  resolveMenuQrMenuLocale,
  usesMenuQrVoseo,
} from '@/domain/menu-qr/menu-qr-copy';
import { IBranch } from '@/interfaces/IBranch';
import { IRestaurant } from '@/interfaces/IRestaurant';
import {
  ensureLocaleMessages,
  i18n,
  SUPPORTED_LANGUAGES,
} from '@/plugins/i18n';
import { useUi } from '@/composition/use-ui';
import { AlertType } from '@/interfaces/IAlert';
import { useRestaurantStore } from '@/stores/restaurant.store';

const SUPPORTED_LOCALE_IDS = SUPPORTED_LANGUAGES.map((language) => language.id);

/**
 * Diner-facing poster/sticker copy: the menu's language when the admin has it
 * (else the UI locale), with voseo for Argentina/Uruguay. Usable from class
 * components too.
 */
export async function resolveMenuQrCopyFor(
  restaurant: IRestaurant | null,
  branch: IBranch | null
): Promise<IMenuQrCopy> {
  let locale = resolveMenuQrCopyLocale(
    restaurant?.languageId,
    i18n.locale,
    SUPPORTED_LOCALE_IDS
  );

  try {
    await ensureLocaleMessages(locale);
  } catch (error) {
    // The menu language's messages could not be loaded: fall back to the UI locale,
    // which is always loaded, rather than leaving the page without copy.
    console.error(error);
    locale = i18n.locale;
  }

  const country = branch?.location?.country ?? restaurant?.country;

  return buildMenuQrCopy(
    (key) => String(i18n.t(key, locale)),
    locale,
    usesMenuQrVoseo(locale, country)
  );
}

export interface IUseMenuQrCopy {
  copy: Ref<IMenuQrCopy | null>;
  /** True when the copy could not be resolved (a toast was shown). */
  failed: Ref<boolean>;
  languageName: ComputedRef<string>;
  /** False when the copy fell back to the UI locale because the menu language has no translations. */
  usesMenuLanguage: Ref<boolean>;
}

export function useMenuQrCopy(): IUseMenuQrCopy {
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const copy = ref<IMenuQrCopy | null>(null);
  const failed = ref(false);
  const usesMenuLanguage = ref(false);
  let generation = 0;

  // Multi-source form: compared element-wise, so replacing the store objects with
  // the same ids does not resolve the copy again.
  watch(
    [
      () => restaurantStore.restaurant?.id,
      () => restaurantStore.selectedBranch?.id,
      () => i18n.locale,
    ],
    async () => {
      const current = ++generation;
      const restaurant = restaurantStore.restaurant;
      const branch = restaurantStore.selectedBranch;

      try {
        const resolved = await resolveMenuQrCopyFor(restaurant, branch);

        if (current !== generation) return;

        copy.value = resolved;
        // Stored with the copy so the note never mixes a new restaurant with old copy.
        usesMenuLanguage.value =
          resolveMenuQrMenuLocale(
            restaurant?.languageId,
            SUPPORTED_LOCALE_IDS
          ) === resolved.locale;
        failed.value = false;
      } catch (error) {
        if (current !== generation) return;

        console.error(error);
        copy.value = null;
        failed.value = true;
        ui.showToast(AlertType.Error, String(i18n.t('qr.failed')));
      }
    },
    { immediate: true }
  );

  const languageName = computed(
    () =>
      SUPPORTED_LANGUAGES.find((language) => language.id === copy.value?.locale)
        ?.name ?? ''
  );

  return { copy, failed, languageName, usesMenuLanguage };
}
